/**
 * Standalone "AIチャットで質問 / プロモーションメニュー版" bottom-edge widget.
 *
 * Drop into any host page with:
 *   <script src="https://ad.rms.rakuten.co.jp/shared/assets/chatbot-button.js" defer></script>
 *
 * Auto-mounts a sticky horizontal pill anchored to the bottom-right corner
 * of the viewport (`right: 24px; bottom: 0`) that links to the shared
 * chatbot page.
 * The widget is fully self-contained:
 *   - Vanilla TS (no React dependency at runtime → tiny bundle).
 *   - All styles isolated in Shadow DOM so the host page's CSS can't leak in.
 *   - Renders an `<a>` (not a `<button>`) so middle-click/right-click open in
 *     a new tab without extra plumbing.
 *   - Two-line label stacked vertically inside a single horizontal pill:
 *     primary text on top ("AIチャットで質問") and a smaller secondary line
 *     beneath it ("プロモーションメニュー版"). Latin runs like "AI" simply
 *     read horizontally as part of the natural text flow — no
 *     vertical-writing-mode trickery needed.
 *
 * Configuration via script tag data attributes:
 *   data-href            URL the button links to. Default:
 *                        "https://ad.rms.rakuten.co.jp/shared/chatbot"
 *                        (the canonical production chatbot URL — works
 *                        from any host origin without further config).
 *   data-label           Visible primary text + base for `aria-label`.
 *                        Default: "AIチャットで質問".
 *   data-sub-label       Visible secondary text rendered on a second line
 *                        beneath the primary label, in a smaller font.
 *                        Default: "プロモーションメニュー版". Pass an empty
 *                        string (`data-sub-label=""`) to render only the
 *                        primary line.
 *   data-popup-width     Popup window width in CSS px. Default: 750.
 *   data-popup-height    Popup window height in CSS px. Default: 800.
 *   data-offset-bottom   CSS length from the bottom edge. Default: "0".
 *   data-offset-left     Optional CSS length anchoring the pill from the
 *                        left edge. When set, takes precedence over
 *                        `data-offset-right` (e.g. for a bottom-left
 *                        launcher).
 *   data-offset-right    CSS length anchoring the pill from the right
 *                        edge. Default: "24px" (canonical bottom-right
 *                        launcher position). Ignored when
 *                        `data-offset-left` is also set.
 *   data-hide-label      "true" to render the icon only (no visible text).
 *                        The label is always applied as `aria-label` for
 *                        screen readers regardless. Default: false (label
 *                        shown alongside the icon).
 *   data-neighbor-selectors
 *                        Comma-separated list of CSS selectors. When the
 *                        host page renders any matching element near the
 *                        bottom of the viewport, the launcher slides
 *                        leftward to sit next to the LEFTMOST visible
 *                        match across all selectors. Default:
 *                        "#survey-popup, #prmm-survey-cont, .prmm-back-to-top". Pass an
 *                        empty string to disable auto-anchoring entirely.
 *   data-neighbor-gap    Pixel gap between the launcher's right edge and
 *                        the leftmost neighbor's left edge when auto-
 *                        anchoring. Default: 16.
 *   data-mobile-breakpoint
 *                        Viewport width in CSS px below which the widget
 *                        is hidden on the auto-mount path. Default: 600
 *                        (mirrors `$mobile-breakpoint-width: 37.5rem` in
 *                        `src/styles/variables.scss` — the shared
 *                        breakpoint the rest of `shared-ui-app` uses for
 *                        mobile-vs-desktop layouts).
 *                        Set to "0" to disable mobile hiding entirely.
 *                        The launcher reactively mounts/unmounts as the
 *                        viewport crosses this breakpoint (e.g. on device
 *                        rotation or window resize), so a phone-sized
 *                        viewport never sees the desktop popup launcher.
 *                        Programmatic `mount()` calls bypass this gate so
 *                        host pages can still force-render if needed.
 *   data-auto-mount      "false" to skip auto-mount; call `mount()` yourself.
 *
 * Auto-mount is also skipped when the pathname includes a `system_error`
 * segment (e.g. `…/<service>/system_error`) so the launcher does not appear
 * on system error pages after redirect. Programmatic `mount()` no-ops in that
 * case as well.
 *
 * The button opens the chatbot in a popup window anchored at the top-left
 * of the screen via `window.open()` (not a new tab). Modifier-key clicks
 * (⌘/Ctrl/Shift) and middle-clicks fall through to the browser's native
 * open-in-new-tab behavior. If the host blocks popups, the host tab
 * navigates to the chatbot URL as a fallback.
 *
 * Or programmatically via `window.RakutenAiChatbotButton`:
 *   RakutenAiChatbotButton.mount({ href: "...", label: "...", subLabel: "..." });
 *   RakutenAiChatbotButton.unmount();
 *   RakutenAiChatbotButton.isMounted();
 *   RakutenAiChatbotButton.version;  // current widget bundle version (string)
 */

import { dispatchRatCustomEvent } from "../utils/ratEvent";

/**
 * Widget bundle version. Bump this manually whenever you ship a meaningful
 * change to the widget (behavior, default styles, public API, etc.) so host
 * pages can verify which build they have loaded:
 *
 *   window.RakutenAiChatbotButton.version  // → "1.0.0"
 *
 * Follows semver:
 *   - MAJOR: breaking change to the script tag API (e.g. renamed data-*,
 *            removed/renamed `mount` option, removed global API method).
 *   - MINOR: additive change (new option, new global method).
 *   - PATCH: bug fix, internal refactor, style tweak with no API impact.
 */
const VERSION = "1.0.2";

const HOST_ELEMENT_ID = "rakuten-ai-chatbot-button-host";
// Absolute production URL so the widget works correctly when embedded on
// host pages on a different origin — a relative path would otherwise
// resolve against the host's origin and 404. Local development can still
// override via `data-href` on the script tag.
const DEFAULT_HREF = "https://ad.rms.rakuten.co.jp/shared/chatbot";
const DEFAULT_LABEL = "AIチャットで質問";
const DEFAULT_SUB_LABEL = "プロモーションメニュー版";
const DEFAULT_OFFSET_BOTTOM = "0";
// Default horizontal anchor: 24 px in from the right edge along the bottom
// — the canonical bottom-right launcher position. Hosts can override with
// `data-offset-left` (which takes precedence) or a custom `data-offset-right`.
const DEFAULT_OFFSET_RIGHT = "24px";
const DEFAULT_POPUP_WIDTH = 750;
const DEFAULT_POPUP_HEIGHT = 800;
// When a host page renders one of these "neighbor" elements near the bottom
// of the viewport, the launcher detects it and slides leftward so it sits
// to the left of (the leftmost of) those elements rather than overlapping
// them. Defaults cover the two common Rakuten host-page neighbors:
//   - `#survey-popup`      — legacy shared bottom-right feedback survey pill
//   - `#prmm-survey-cont`  — current shared bottom-right feedback survey pill
//   - `.prmm-back-to-top`  — the shared bottom-right "back to top" button
// Hosts can override the entire list via `data-neighbor-selectors`
// (comma-separated CSS selectors) or pass an empty string to disable
// auto-anchoring entirely.
const DEFAULT_NEIGHBOR_SELECTORS: readonly string[] = [
  "#survey-popup",
  "#prmm-survey-cont",
  ".prmm-back-to-top",
];
// Pixel gap between the launcher's right edge and the leftmost neighbor's
// left edge when auto-anchoring. Hosts can tune this with `data-neighbor-gap`.
const DEFAULT_NEIGHBOR_GAP_PX = 16;
// Some neighbors should win regardless of horizontal order. When any visible
// near-bottom element matches one of these selectors, we anchor to it first.
// Current requirement: `.prmm-back-to-top` must win over survey widgets when
// both are present.
const PRIORITY_NEIGHBOR_SELECTORS: readonly string[] = [".prmm-back-to-top"];
// Viewport width (CSS px) below which the auto-mount path skips rendering
// the launcher. The chatbot opens in a 750 x 800 popup window, which makes
// no sense on a phone-sized viewport — the launcher would just trigger a
// degraded in-tab navigation fallback.
//
// SOURCE OF TRUTH: `$mobile-breakpoint-width` in
// `src/styles/variables.scss` (`37.5rem` → 600 px at the default 16 px
// root font-size). Mirrored here as a plain number because the widget is
// a standalone IIFE bundle with zero runtime deps and cannot import from
// the Sass layer. Keep the two in sync if the SCSS variable changes —
// no automated check enforces this.
//
// Hosts can override with `data-mobile-breakpoint`, or pass `0` to
// disable mobile hiding.
const DEFAULT_MOBILE_BREAKPOINT_PX = 600;
// Auto-anchoring is gated on the neighbor actually living "at the bottom"
// of the page. We use a coarse heuristic: the neighbor's bottom edge must
// be in the lower half of the viewport. Matching elements that happen to
// render in the page header or mid-content are intentionally left alone.
const NEIGHBOR_NEAR_BOTTOM_THRESHOLD_RATIO = 0.5;
// And there has to be at least this much room between the viewport's left
// edge and the neighbor's left edge for the launcher to actually fit.
// Full-width banners or elements that take up most of the viewport width
// fall through to the default position rather than getting pushed
// off-screen.
const NEIGHBOR_MIN_LEFT_ROOM_PX = 120;
// Fixed window name → re-clicking the button focuses the existing popup
// instead of stacking duplicates.
const POPUP_WINDOW_NAME = "RakutenAiChatbotWindow";

// RAT tagging for the launcher click. Sent on every primary-button click via
// `addCustomEvent` so the event is logged regardless of the host page's
// cached `ssc` / `pgn` values — and silently no-ops on host pages that don't
// load `rat-main.js` (the helper guards `window.RAT?.addCustomEvent`).
const RAT_COMPONENT_ID = "btnChatbot";
const RAT_PAGE_NAME = "Chat_Link";
const RAT_SITE_NAME = "chat_button";

export type MountOptions = {
  href?: string;
  /** Primary label text. Default "AIチャットで質問". */
  label?: string;
  /**
   * Optional secondary label, rendered on a second line beneath `label` in
   * a smaller font. Default "プロモーションメニュー版". Pass an empty
   * string to render only the primary line.
   */
  subLabel?: string;
  /** Popup window width in CSS pixels. Default 750. */
  popupWidth?: number;
  /** Popup window height in CSS pixels. Default 800. */
  popupHeight?: number;
  /** Any valid CSS length. Distance from the bottom edge. Default "0". */
  offsetBottom?: string;
  /**
   * Optional CSS length anchoring the pill from the left edge. When set,
   * takes precedence over `offsetRight`. When omitted, the pill anchors
   * from the right edge using `offsetRight`.
   */
  offsetLeft?: string;
  /**
   * CSS length anchoring the pill from the right edge. Default "24px"
   * (the canonical bottom-right launcher position). Ignored when
   * `offsetLeft` is also set.
   */
  offsetRight?: string;
  /**
   * When true, render only the sparkle icon (no visible label text). The
   * `label` (and `subLabel`, if set) is still applied as `aria-label` so
   * screen readers and tooltip tools can announce the button. Default
   * false (label shown alongside the icon).
   */
  hideLabel?: boolean;
  /**
   * CSS selectors of "neighbor" elements on the host page that the launcher
   * should sit beside (to their left, with a small gap) when they're
   * rendered near the bottom of the viewport. The launcher anchors to the
   * LEFT of the leftmost visible match across all selectors, so a host
   * page that simultaneously renders multiple bottom-right widgets (e.g.
   * a feedback survey pill plus a back-to-top button) can have all of
   * them line up cleanly with the launcher to their left.
   *
   * Default: `["#survey-popup", "#prmm-survey-cont", ".prmm-back-to-top"]`.
   * Pass an empty
   * array to disable auto-anchoring entirely. The launcher observes each
   * matching element's size + DOM lifecycle and reflows in real time as
   * elements appear, resize, or are dismissed.
   */
  neighborSelectors?: readonly string[];
  /**
   * Gap in CSS pixels between the launcher's right edge and the leftmost
   * matching neighbor's left edge when auto-anchoring kicks in. Default
   * 16.
   */
  neighborGap?: number;
  /**
   * Viewport width threshold (CSS px) below which the auto-mount path
   * hides the launcher. Default 600, mirroring
   * `$mobile-breakpoint-width: 37.5rem` in
   * `src/styles/variables.scss` so the widget appears/disappears on the
   * same boundary `shared-ui-app` uses for the rest of its mobile-vs-
   * desktop layouts. Pass `0` to disable mobile hiding entirely.
   *
   * Only consulted by the auto-mount path, which sets up a `matchMedia`
   * listener so the launcher reactively appears/disappears as the
   * viewport crosses the breakpoint (e.g. on device rotation or browser
   * window resize). Programmatic `mount()` calls bypass this gate, so
   * host pages with their own responsive logic retain full control.
   */
  mobileBreakpoint?: number;
};

export type ChatbotButtonApi = {
  mount: (options?: MountOptions) => void;
  unmount: () => void;
  isMounted: () => boolean;
  /**
   * Widget bundle version (see `VERSION` constant). Useful for host pages
   * to assert a minimum version, and for support / debugging to confirm
   * which build is currently loaded in production.
   */
  readonly version: string;
};

declare global {
  interface Window {
    RakutenAiChatbotButton?: ChatbotButtonApi;
  }
}

/**
 * The Rakuten AI sparkle mark, mirrored from
 * `src/chatbot/ChatIcons/RakutenAiSparkleIcon.tsx`. Inlined as a string
 * to keep the widget bundle dependency-free.
 */
const SPARKLE_SVG = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.2362 12.7673H7.21448L10.4566 21.9997H11.2362V12.7673Z" fill="#F60497"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.2362 11.2361V7.21436L2.00031 10.4565V11.2361H11.2362Z" fill="#0E98EC"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.7673 11.2362H16.7855L13.5434 2.00024H12.7673V11.2362Z" fill="#874BC3"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.7673 12.7673V16.7856L21.9997 13.5435V12.7673H12.7673Z" fill="#C027B0"/>
</svg>`;

/**
 * Shadow DOM stylesheet — bottom-edge sticky pill design.
 *
 * Layout: horizontal pill flush against the bottom edge of the viewport,
 * with only the TOP corners rounded (the bottom side reads as "attached"
 * to the floor). Icon sits on the leading edge; the label area is a
 * vertical flex column that stacks the primary line on top and an
 * optional smaller secondary line beneath it.
 *
 * Hover behavior: a one-shot sparkle spin plus a small upward slide,
 * suggesting the pill can be "lifted" off the floor. The pill snaps back
 * down on `:active` for a press feel.
 */
const SHADOW_STYLES = `
  :host {
    /* Reset inheritable properties so host page CSS can't bleed into the
       Shadow DOM (font-size, color, line-height, etc.). */
    all: initial;
    display: block;
  }

  .chatbot-button {
    --rakuten-ai-purple: #874bc3;
    --rakuten-ai-purple-focus: #630cc0;
    --rakuten-ai-bg: #ffffff;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    box-sizing: border-box;
    padding: 0.625rem 0.5rem;
    background: var(--rakuten-ai-bg);
    color: var(--rakuten-ai-purple);
    border: 1px solid var(--rakuten-ai-purple);
    /* Hide the bottom border — the pill is flush against the viewport floor. */
    border-bottom: none;
    /* Round only the top corners; bottom edge stays flat against the floor. */
    border-radius: 12px 12px 0 0;
    font-family:
      -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue",
      "Hiragino Sans", "Yu Gothic", Meiryo, sans-serif;
    font-size: 14px;
    font-weight: normal;
    line-height: 1.3;
    text-decoration: none;
    cursor: pointer;
    /* Slightly translucent at rest so underlying page content remains
       legible behind the pill; full opacity on hover/focus so the button
       reads as fully active when the user engages with it. */
    opacity: 1;
    transition: transform 0.15s ease, opacity 0.15s ease;
  }

  .chatbot-button:hover,
  .chatbot-button:focus-visible {
    opacity: 1;
  }

  .chatbot-button:hover {
    /* Lift a few pixels off the floor as a "peek up" affordance. */
    transform: translateY(-3px);
  }

  .chatbot-button:active {
    /* Snap back down against the floor for a press-down feel. */
    transform: translateY(0);
  }

  .chatbot-button:focus-visible {
    outline: 2px solid var(--rakuten-ai-purple-focus);
    outline-offset: 2px;
  }

  .chatbot-button__icon {
    display: inline-flex;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    transform-origin: 50% 50%;
  }

  .chatbot-button__icon svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .chatbot-button__label {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    /* Tight line-height keeps the two-line stack visually compact so the
       pill doesn't grow disproportionately tall. */
    line-height: 1.15;
    white-space: nowrap;
  }

  /* Both label lines render identically — same font-size, font-weight,
     and color — and inherit those properties from the parent
     .chatbot-button. Per-line style rules are intentionally absent so
     the two spans read as a single visually uniform two-line block. The
     1 px margin-top below is the only per-line override and just adds a
     hairline gap between the lines for readability without breaking
     visual uniformity. */
  .chatbot-button__sub {
    margin-top: 1px;
  }

  @keyframes chatbot-button-sparkle-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  .chatbot-button:hover .chatbot-button__icon {
    animation: chatbot-button-sparkle-spin 0.6s ease-in-out 1;
  }

  @media (prefers-reduced-motion: reduce) {
    .chatbot-button:hover .chatbot-button__icon {
      animation: none;
    }
    .chatbot-button:hover {
      transform: none;
    }
  }

  @media (max-width: 480px) {
    .chatbot-button {
      padding: 0.625rem;
      gap: 0.375rem;
      font-size: 13px;
    }
    .chatbot-button__icon {
      width: 18px;
      height: 18px;
    }
  }
`;

let hostElement: HTMLElement | null = null;
/**
 * Live reference to the popup we previously opened, so re-clicks can just
 * `.focus()` it instead of navigating it back to `href` (which would reload
 * the chatbot SPA and wipe in-memory UI state). Cleared lazily once the
 * popup reports `.closed === true`.
 */
let popupWindow: Window | null = null;

function readScriptOptions(): MountOptions {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return {};

  const options: MountOptions = {};
  const { dataset } = script;

  if (dataset.href) options.href = dataset.href;
  if (dataset.label) options.label = dataset.label;
  // Use !== undefined so an explicit `data-sub-label=""` is preserved as
  // an opt-out (renders only the primary line) instead of falling back to
  // the default secondary text.
  if (dataset.subLabel !== undefined) options.subLabel = dataset.subLabel;
  if (dataset.offsetBottom) options.offsetBottom = dataset.offsetBottom;
  if (dataset.offsetLeft) options.offsetLeft = dataset.offsetLeft;
  if (dataset.offsetRight) options.offsetRight = dataset.offsetRight;
  // Default is false (label shown); host pages opt into icon-only with "true".
  if (dataset.hideLabel === "true") options.hideLabel = true;
  // Use !== undefined so an explicit `data-neighbor-selectors=""` is
  // preserved as an opt-out (disables auto-anchoring) instead of falling
  // back to the defaults. Selectors are comma-separated; empty entries
  // (e.g. trailing commas, double commas) are filtered out.
  if (dataset.neighborSelectors !== undefined) {
    options.neighborSelectors = dataset.neighborSelectors
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const gapNum = Number(dataset.neighborGap);
  if (Number.isFinite(gapNum) && gapNum >= 0) {
    options.neighborGap = gapNum;
  }

  // `0` is a meaningful value — it disables mobile hiding entirely — so we
  // accept any finite, non-negative number here rather than truthy-checking.
  if (dataset.mobileBreakpoint !== undefined) {
    const breakpointNum = Number(dataset.mobileBreakpoint);
    if (Number.isFinite(breakpointNum) && breakpointNum >= 0) {
      options.mobileBreakpoint = breakpointNum;
    }
  }

  const widthNum = Number(dataset.popupWidth);
  if (Number.isFinite(widthNum) && widthNum > 0) options.popupWidth = widthNum;

  const heightNum = Number(dataset.popupHeight);
  if (Number.isFinite(heightNum) && heightNum > 0) {
    options.popupHeight = heightNum;
  }

  return options;
}

/**
 * Returns true when the current document URL matches the chatbot page the
 * button would navigate to. Used to skip auto-mount on the chatbot page
 * itself — otherwise the widget would also render inside the popup it
 * opens, since the chatbot is part of the same SPA shell that hosts this
 * script. Compares origin + normalized pathname; query/hash are ignored.
 */
function isOnTargetChatbotPage(href: string): boolean {
  try {
    const target = new URL(href, window.location.href);
    const normalize = (p: string) => p.replace(/\/+$/, "") || "/";
    return (
      target.origin === window.location.origin &&
      normalize(target.pathname) === normalize(window.location.pathname)
    );
  } catch {
    return false;
  }
}

/**
 * True when the pathname contains a `system_error` segment (e.g.
 * `https://host/<service>/system_error`). Uses segment matching so paths like
 * `/not_system_error` do not match. Trailing slashes are ignored.
 */
function isOnSystemErrorPage(): boolean {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path.split("/").filter(Boolean).includes("system_error");
}

function shouldAutoMount(): boolean {
  const script = document.currentScript as HTMLScriptElement | null;
  // Auto-mount unless the host page explicitly opts out.
  return script?.dataset.autoMount !== "false";
}

/**
 * Opens the chatbot URL as a popup window anchored at the top-left of the
 * screen. If a popup we previously opened is still alive, just focuses it
 * (preserving the chatbot's UI state) instead of reopening / renavigating.
 * Falls back to navigating the host tab if the browser blocks the popup,
 * so the user always reaches the chatbot rather than getting a no-op click.
 */
function openChatbotPopup(href: string, width: number, height: number): void {
  // Re-click while an existing popup is still open: just bring it forward.
  // We deliberately do NOT call `window.open(href, ...)` again because
  // passing a URL to a named-window target reloads it to that URL, which
  // would reset any in-progress chat / scroll position.
  // `.closed` is one of the few popup properties readable cross-origin,
  // so this is safe even when the host page and chatbot are on different
  // origins.
  if (popupWindow && !popupWindow.closed) {
    try {
      popupWindow.focus();
      return;
    } catch {
      /* focus() can throw in rare cross-origin edge cases — fall through
         and reopen below. */
    }
  }

  // Anchor the popup at the top-left of the screen.
  const left = 0;
  const top = 0;

  // `popup=yes` hint asks modern browsers (Chrome 99+, Firefox, Safari) to
  // render this as a discrete popup window rather than another tab.
  // `resizable=no` locks the popup to the configured width/height so the
  // chatbot UI is always presented at its intended dimensions. Note: most
  // modern browsers honor this only as a hint — desktop Chrome/Firefox
  // typically still allow user resize regardless.
  const features = `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=no,scrollbars=yes`;

  const popup = window.open(href, POPUP_WINDOW_NAME, features);

  if (popup) {
    popup.focus();
    popupWindow = popup;
    return;
  }

  // Popup blocked — drop our stale reference and gracefully degrade to
  // in-tab navigation.
  popupWindow = null;
  window.location.href = href;
}

// ----------------------------------------------------------------------------
// Bottom-edge neighbor auto-anchoring
// ----------------------------------------------------------------------------
//
// When the host page renders one or more "neighbor" elements at the bottom
// of the viewport (e.g. `#survey-popup`, `.prmm-back-to-top`, or anything
// else the host configures via `neighborSelectors`), the launcher
// automatically slides leftward so it sits to the LEFT of the leftmost
// visible match. This lets a host page stack multiple bottom-right widgets
// without any of them overlapping.
//
// The launcher's right edge stays `neighborGap` pixels (default 16) from
// the leftmost neighbor's left edge. We deliberately do NOT derive the
// launcher's bottom from the neighbor's rect — the launcher keeps its
// configured `offsetBottom` (default "0") so it stays glued to the floor
// even when a neighbor has internal padding/border or sits at a negative
// `bottom`. The pills share a baseline because they're both bottom-
// anchored to the same value.
//
// We listen to three signals and coalesce them into a single rAF-batched
// reposition tick:
//   1. `MutationObserver` on the body subtree — catches neighbors being
//      added or removed and any visibility-affecting attribute changes
//      (`style`, `class`, `hidden`, `id`).
//   2. `ResizeObserver` on each currently matched neighbor — catches size
//      changes (e.g. popup expanding / collapsing) without depending on
//      attribute mutations.
//   3. `window resize` — recomputes whenever the viewport itself changes.
//
// Hosts that don't want this behavior can pass `data-neighbor-selectors=""`
// (empty string) which disables the detection entirely.

type ResolvedPosition = {
  offsetBottom: string;
  offsetLeft: string | undefined;
  offsetRight: string;
  neighborSelectors: readonly string[];
  neighborGap: number;
};

let currentPosition: ResolvedPosition | null = null;
// We track ALL currently matched neighbors (not just the leftmost) so the
// ResizeObserver fires regardless of which one resizes — a non-leftmost
// match growing wider can change which match is the leftmost on the next
// tick, and we need to catch that.
let observedNeighborEls = new Set<HTMLElement>();
let neighborResizeObserver: ResizeObserver | null = null;
let neighborMutationObserver: MutationObserver | null = null;
let neighborWindowResizeListener: (() => void) | null = null;
let pendingRepositionFrame: number | null = null;

function isNeighborVisible(el: HTMLElement): boolean {
  // offsetWidth / offsetHeight are 0 for `display: none`, the `hidden`
  // attribute, and detached subtrees. This is the cheapest reliable
  // visibility check that doesn't trigger an extra layout pass beyond
  // what we'd already do reading `getBoundingClientRect()`.
  return el.offsetWidth > 0 && el.offsetHeight > 0;
}

function collectNeighbors(selectors: readonly string[]): HTMLElement[] {
  // Walks every configured selector and collects the union of matching
  // HTMLElements. We dedupe across selectors so an element matched by
  // both an ID and a class selector isn't double-counted, and we
  // gracefully skip selectors that throw — querySelectorAll throws on
  // syntactically invalid selectors and we don't want one bad host
  // config to take down the whole launcher.
  const out: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();
  for (const selector of selectors) {
    if (!selector) continue;
    let matches: NodeListOf<Element>;
    try {
      matches = document.querySelectorAll(selector);
    } catch {
      continue;
    }
    matches.forEach((el) => {
      if (el instanceof HTMLElement && !seen.has(el)) {
        seen.add(el);
        out.push(el);
      }
    });
  }
  return out;
}

function findLeftmostAnchor(
  neighbors: readonly HTMLElement[]
): { el: HTMLElement; rect: DOMRect } | null {
  // Returns the best anchor candidate that passes the "near bottom" + "has
  // room to the left" gates:
  //   1) Prefer selectors in `PRIORITY_NEIGHBOR_SELECTORS` (in order).
  //   2) For ties, choose the leftmost candidate.
  // This keeps `.prmm-back-to-top` as the positioning source of truth when it
  // coexists with survey pills.
  let best: { el: HTMLElement; rect: DOMRect } | null = null;
  let bestPriority = Infinity;
  let bestLeft = Infinity;

  const getPriority = (el: HTMLElement): number => {
    for (let i = 0; i < PRIORITY_NEIGHBOR_SELECTORS.length; i += 1) {
      const selector = PRIORITY_NEIGHBOR_SELECTORS[i];
      try {
        if (el.matches(selector)) return i;
      } catch {
        // Invalid selector in priority config: ignore and continue.
      }
    }
    return PRIORITY_NEIGHBOR_SELECTORS.length;
  };

  for (const el of neighbors) {
    if (!isNeighborVisible(el)) continue;
    const rect = el.getBoundingClientRect();
    const isNearBottom =
      rect.bottom > window.innerHeight * NEIGHBOR_NEAR_BOTTOM_THRESHOLD_RATIO;
    const hasRoomToLeft = rect.left >= NEIGHBOR_MIN_LEFT_ROOM_PX;
    if (!isNearBottom || !hasRoomToLeft) continue;
    const priority = getPriority(el);
    if (
      priority < bestPriority ||
      (priority === bestPriority && rect.left < bestLeft)
    ) {
      bestPriority = priority;
      bestLeft = rect.left;
      best = { el, rect };
    }
  }
  return best;
}

function syncNeighborResizeObservers(neighbors: readonly HTMLElement[]): void {
  // Add/remove ResizeObserver bindings to match the current set of
  // detected neighbors. We compare by element identity so a stable
  // element is observed exactly once across many reposition ticks.
  if (typeof ResizeObserver === "undefined") return;

  const next = new Set<HTMLElement>(neighbors);

  for (const el of observedNeighborEls) {
    if (!next.has(el)) {
      neighborResizeObserver?.unobserve(el);
    }
  }

  if (next.size > 0 && !neighborResizeObserver) {
    neighborResizeObserver = new ResizeObserver(scheduleReposition);
  }

  for (const el of next) {
    if (!observedNeighborEls.has(el)) {
      neighborResizeObserver?.observe(el);
    }
  }

  observedNeighborEls = next;
}

function applyHostPosition(): void {
  if (!hostElement || !currentPosition) return;

  const {
    offsetBottom,
    offsetLeft,
    offsetRight,
    neighborSelectors,
    neighborGap,
  } = currentPosition;

  // Custom `offsetLeft` from the host overrides neighbor auto-anchoring:
  // if the host has explicitly placed the launcher on the LEFT side of
  // the screen, the bottom-right neighbors wouldn't overlap with it and
  // there's no need to reposition. An empty `neighborSelectors` array is
  // the host's explicit opt-out signal.
  const neighbors =
    !offsetLeft && neighborSelectors.length > 0
      ? collectNeighbors(neighborSelectors)
      : [];

  syncNeighborResizeObservers(neighbors);

  const anchor = findLeftmostAnchor(neighbors);
  if (anchor) {
    const rightOffset = window.innerWidth - anchor.rect.left + neighborGap;
    hostElement.style.cssText = `
      position: fixed;
      bottom: ${offsetBottom};
      right: ${rightOffset}px;
      z-index: 2147483647;
    `;
    return;
  }

  // Default positioning: no neighbors, all matches hidden, none at the
  // bottom, or none with enough room to the left.
  const horizontalCss = offsetLeft
    ? `left: ${offsetLeft};`
    : `right: ${offsetRight};`;
  hostElement.style.cssText = `
    position: fixed;
    bottom: ${offsetBottom};
    ${horizontalCss}
    z-index: 2147483647;
  `;
}

function scheduleReposition(): void {
  if (pendingRepositionFrame !== null) return;
  pendingRepositionFrame = requestAnimationFrame(() => {
    pendingRepositionFrame = null;
    applyHostPosition();
  });
}

function setupNeighborTracking(): void {
  // Bail out cleanly when the host has disabled neighbor anchoring (empty
  // selector list) — no need to wire any observers.
  if (!currentPosition || currentPosition.neighborSelectors.length === 0) {
    return;
  }

  if (typeof MutationObserver !== "undefined" && document.body) {
    neighborMutationObserver = new MutationObserver(scheduleReposition);
    // Watch the entire body subtree for childList + a narrow set of
    // attribute changes that could affect a neighbor's identity or
    // visibility. Other attribute changes are ignored to keep the
    // observer's overhead bounded on busy host pages.
    neighborMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["id", "class", "style", "hidden"],
    });
  }

  neighborWindowResizeListener = scheduleReposition;
  window.addEventListener("resize", neighborWindowResizeListener);
}

function teardownNeighborTracking(): void {
  neighborMutationObserver?.disconnect();
  neighborMutationObserver = null;
  neighborResizeObserver?.disconnect();
  neighborResizeObserver = null;
  observedNeighborEls = new Set();
  if (neighborWindowResizeListener) {
    window.removeEventListener("resize", neighborWindowResizeListener);
    neighborWindowResizeListener = null;
  }
  if (pendingRepositionFrame !== null) {
    cancelAnimationFrame(pendingRepositionFrame);
    pendingRepositionFrame = null;
  }
  currentPosition = null;
}

function mount(options: MountOptions = {}): void {
  if (hostElement) return;
  if (isOnSystemErrorPage()) return;

  const {
    href = DEFAULT_HREF,
    label = DEFAULT_LABEL,
    subLabel = DEFAULT_SUB_LABEL,
    popupWidth = DEFAULT_POPUP_WIDTH,
    popupHeight = DEFAULT_POPUP_HEIGHT,
    offsetBottom = DEFAULT_OFFSET_BOTTOM,
    offsetLeft,
    offsetRight = DEFAULT_OFFSET_RIGHT,
    hideLabel = false,
    neighborSelectors = DEFAULT_NEIGHBOR_SELECTORS,
    neighborGap = DEFAULT_NEIGHBOR_GAP_PX,
  } = options;

  hostElement = document.createElement("div");
  hostElement.id = HOST_ELEMENT_ID;
  // Cache the resolved positioning options so the auto-anchor logic below
  // can fall back to the host's defaults whenever every matched neighbor
  // is removed, hidden, or moved out of the bottom-right area.
  currentPosition = {
    offsetBottom,
    offsetLeft,
    offsetRight,
    neighborSelectors,
    neighborGap,
  };
  // Apply the initial position synchronously so the launcher mounts in the
  // correct place from the first paint (no flicker between default and
  // auto-anchored positions when a matching neighbor is already present).
  applyHostPosition();

  const shadow = hostElement.attachShadow({ mode: "open" });

  const styleEl = document.createElement("style");
  styleEl.textContent = SHADOW_STYLES;
  shadow.appendChild(styleEl);

  const link = document.createElement("a");
  link.className = "chatbot-button";
  link.id = RAT_COMPONENT_ID;
  link.href = href;
  // Keep `rel` for safety on middle-click / right-click → "open in new tab",
  // since those bypass our click handler and use the anchor's native flow.
  link.rel = "noopener noreferrer";
  // Combined aria-label so screen readers announce both lines as a single
  // button name (e.g. "AIチャットで質問 プロモーションメニュー版, link").
  const ariaLabel = subLabel ? `${label} ${subLabel}` : label;
  link.setAttribute("aria-label", ariaLabel);

  link.addEventListener("click", (event) => {
    // Fire RAT BEFORE the modifier-key short-circuit so the event is logged
    // regardless of whether the user opens the chatbot in our popup, a real
    // new tab (⌘/Ctrl/Shift/middle-click), or via keyboard Enter — every
    // primary-button click against the launcher is a meaningful engagement.
    dispatchRatCustomEvent({
      componentId: RAT_COMPONENT_ID,
      siteName: RAT_SITE_NAME,
      pageName: RAT_PAGE_NAME,
    });

    // Honor modifier-key + middle-click semantics: those mean the user
    // explicitly wants a real new tab/window, not our popup.
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    openChatbotPopup(href, popupWidth, popupHeight);
  });

  const iconWrap = document.createElement("span");
  iconWrap.className = "chatbot-button__icon";
  // Static, vendor-controlled SVG markup — safe to set via innerHTML.
  iconWrap.innerHTML = SPARKLE_SVG;
  link.appendChild(iconWrap);

  if (!hideLabel) {
    const labelEl = document.createElement("span");
    labelEl.className = "chatbot-button__label";

    const primaryEl = document.createElement("span");
    primaryEl.className = "chatbot-button__primary";
    primaryEl.textContent = label;
    labelEl.appendChild(primaryEl);

    if (subLabel) {
      const subEl = document.createElement("span");
      subEl.className = "chatbot-button__sub";
      subEl.textContent = subLabel;
      labelEl.appendChild(subEl);
    }

    link.appendChild(labelEl);
  }

  shadow.appendChild(link);
  document.body.appendChild(hostElement);

  // Wire up the neighbor auto-anchor observers AFTER the host element is
  // in the DOM. Doing it earlier would mean the very first MutationObserver
  // batch fires while the host is still being inserted, which is harmless
  // but creates an unnecessary reposition tick.
  setupNeighborTracking();
}

function unmount(): void {
  if (!hostElement) return;
  // Tear observers down BEFORE removing the host element so the body
  // childList change we're about to make doesn't trigger a spurious
  // reposition tick on a host that's no longer there.
  teardownNeighborTracking();
  hostElement.remove();
  hostElement = null;
}

function isMounted(): boolean {
  return hostElement !== null;
}

/**
 * Wires the auto-mount entry point.
 *
 * Two pieces of behavior on top of plain `mount(options)`:
 *
 *   1. Defers the initial mount until `DOMContentLoaded` if the script
 *      runs while the document is still parsing — `mount()` needs
 *      `document.body` to attach the host element to.
 *   2. Hides the launcher on mobile-sized viewports (default `< 600px`
 *      wide, mirroring `$mobile-breakpoint-width` in
 *      `src/styles/variables.scss`; configurable via `mobileBreakpoint`,
 *      set `0` to disable). A `matchMedia` listener tracks viewport
 *      changes so the launcher reactively appears/disappears as the
 *      breakpoint is crossed — covers both device rotation and desktop
 *      browser window resize (common during dev / responsive testing).
 *
 * The `matchMedia` listener intentionally calls the public `mount()` /
 * `unmount()` so manual host calls and the responsive logic stay in
 * sync — e.g. if a host page programmatically `unmount()`s the widget
 * while the viewport is currently desktop-sized, a later resize down to
 * mobile and back to desktop will re-mount it. This keeps the responsive
 * behavior predictable as a single source of truth.
 */
function setupAutoMount(scriptOptions: MountOptions): void {
  const breakpointPx =
    scriptOptions.mobileBreakpoint ?? DEFAULT_MOBILE_BREAKPOINT_PX;
  const mobileHidingEnabled = Number.isFinite(breakpointPx) && breakpointPx > 0;

  // `(max-width: Npx)` matches widths <= N. We subtract 1 so the breakpoint
  // value itself counts as "desktop" — i.e. `mobileBreakpoint: 600` means
  // 600 px is the smallest desktop width, which matches the SCSS convention
  // `@media (min-width: $mobile-breakpoint-width)` for desktop-and-up.
  const mq = mobileHidingEnabled
    ? window.matchMedia(`(max-width: ${breakpointPx - 1}px)`)
    : null;

  const tryMount = () => {
    if (mq?.matches) return;
    mount(scriptOptions);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryMount, { once: true });
  } else {
    tryMount();
  }

  if (!mq) return;

  mq.addEventListener("change", () => {
    if (mq.matches) {
      if (isMounted()) unmount();
    } else if (!isMounted()) {
      // Document may still be parsing on a very fast viewport change —
      // defer to DOMContentLoaded so `mount()` has a body to attach to.
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", tryMount, {
          once: true,
        });
      } else {
        mount(scriptOptions);
      }
    }
  });
}

if (shouldAutoMount()) {
  const scriptOptions = readScriptOptions();
  // Skip auto-mount on the chatbot page itself; otherwise the widget would
  // appear inside the popup window it just opened (shared SPA index.html).
  // Hosts that explicitly want the button on the chatbot page can still
  // call `window.RakutenAiChatbotButton.mount()` themselves.
  const targetHref = scriptOptions.href ?? DEFAULT_HREF;
  if (!isOnTargetChatbotPage(targetHref) && !isOnSystemErrorPage()) {
    setupAutoMount(scriptOptions);
  }
}

// Named exports are auto-attached to `window.RakutenAiChatbotButton` by the
// Vite IIFE wrapper. `VERSION` is exposed as the lowercase `version`
// property to match the public API documented in the header / README.
export { mount, unmount, isMounted, VERSION as version };
